/**
 * SheetEditor — a small rich-text "sheet" editor (modal).
 * Uses a contentEditable surface + document.execCommand for basic formatting
 * (bold / italic / underline / lists / heading), so it needs no extra deps.
 * Supports @-mentions: typing "@" opens a user picker and inserts an atomic
 * mention token (<span data-mention="uuid">@Name</span>) — the backend reads
 * data-mention="uuid" to notify the mentioned user. Saves HTML through onSave.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  XMarkIcon, BoldIcon, ItalicIcon, UnderlineIcon, ListBulletIcon, NumberedListIcon, H1Icon,
} from '@heroicons/react/24/outline';
import { useUsersForBoards, type BoardUser } from './api';

interface ToolBtn { cmd: string; arg?: string; title: string; Icon: React.FC<{ className?: string }>; label?: string }
const TOOLS: ToolBtn[] = [
  { cmd: 'bold', title: 'Gras (Ctrl+B)', Icon: BoldIcon },
  { cmd: 'italic', title: 'Italique (Ctrl+I)', Icon: ItalicIcon },
  { cmd: 'underline', title: 'Souligné (Ctrl+U)', Icon: UnderlineIcon },
  { cmd: 'formatBlock', arg: 'H3', title: 'Titre', Icon: H1Icon },
  { cmd: 'insertUnorderedList', title: 'Liste à puces', Icon: ListBulletIcon },
  { cmd: 'insertOrderedList', title: 'Liste numérotée', Icon: NumberedListIcon },
];

const userLabel = (u: BoardUser) => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;

export const SheetEditor: React.FC<{
  title?: string;
  initialHtml: string;
  onSave: (html: string) => void;
  onClose: () => void;
}> = ({ title, initialHtml, onSave, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { data: users = [] } = useUsersForBoards();
  const [mention, setMention] = useState<{ query: string; top: number; left: number } | null>(null);

  useEffect(() => {
    if (ref.current) { ref.current.innerHTML = initialHtml || ''; ref.current.focus(); }
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') { if (mention) setMention(null); else onClose(); } };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = (t: ToolBtn) => { document.execCommand(t.cmd, false, t.arg); ref.current?.focus(); };

  const filtered = useMemo(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return users.filter(u => !q || userLabel(u).toLowerCase().includes(q) || u.email.toLowerCase().includes(q)).slice(0, 8);
  }, [mention, users]);

  // Detect a trailing "@query" before the caret and surface the picker.
  const onInput = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) { setMention(null); return; }
    const node = sel.anchorNode;
    if (!node || node.nodeType !== Node.TEXT_NODE) { setMention(null); return; }
    const before = (node.textContent || '').slice(0, sel.anchorOffset);
    const m = /(^|\s)@([\p{L}\w.\-]*)$/u.exec(before);
    if (!m) { setMention(null); return; }
    const r = sel.getRangeAt(0).cloneRange();
    const rect = r.getBoundingClientRect();
    setMention({ query: m[2], top: rect.bottom + 4, left: rect.left });
  };

  const insertMention = (u: BoardUser) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { setMention(null); return; }
    const node = sel.anchorNode;
    if (!node || node.nodeType !== Node.TEXT_NODE) { setMention(null); return; }
    const offset = sel.anchorOffset;
    const before = (node.textContent || '').slice(0, offset);
    const m = /@([\p{L}\w.\-]*)$/u.exec(before);
    if (!m) { setMention(null); return; }
    // select the "@query" text and replace it with the mention token + a space
    const start = offset - m[0].length;
    const range = document.createRange();
    range.setStart(node, start);
    range.setEnd(node, offset);
    range.deleteContents();
    const span = document.createElement('span');
    span.className = 'bd-mention';
    span.setAttribute('data-mention', u.id);
    span.setAttribute('contenteditable', 'false');
    span.textContent = `@${userLabel(u)}`;
    const space = document.createTextNode(' ');
    range.insertNode(space);
    range.insertNode(span);
    // place caret after the inserted space
    const after = document.createRange();
    after.setStartAfter(space);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
    setMention(null);
    ref.current?.focus();
  };

  return createPortal(
    <div className="bd-modal-backdrop" onClick={onClose}>
      <div className="bd-modal bd-sheet-modal" role="dialog" aria-label={title || 'Feuille de texte'} onClick={e => e.stopPropagation()}>
        <div className="bd-modal-header">
          <h3>{title || 'Feuille de texte'}</h3>
          <button onClick={onClose} title="Fermer"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="bd-sheet-toolbar">
          {TOOLS.map((t, i) => {
            const I = t.Icon;
            return (
              <button key={i} type="button" className="bd-sheet-tool" title={t.title}
                onMouseDown={e => { e.preventDefault(); run(t); }}>
                <I className="w-4 h-4" />
              </button>
            );
          })}
          <span className="bd-sheet-sep" />
          <button type="button" className="bd-sheet-tool" title="Effacer la mise en forme"
            onMouseDown={e => { e.preventDefault(); document.execCommand('removeFormat'); ref.current?.focus(); }}>
            Tx
          </button>
          <span className="bd-sheet-hint">@ pour mentionner</span>
        </div>
        <div
          ref={ref}
          className="bd-sheet-area"
          contentEditable
          suppressContentEditableWarning
          data-placeholder="Écrivez ici… (@ pour mentionner quelqu'un)"
          onInput={onInput}
          onKeyUp={onInput}
        />
        <div className="bd-modal-footer">
          <button className="bd-btn-ghost" onClick={onClose}>Annuler</button>
          <button className="bd-btn-primary" onClick={() => onSave(ref.current?.innerHTML || '')}>Enregistrer</button>
        </div>
      </div>

      {mention && filtered.length > 0 && createPortal(
        <div className="bd-mention-pop" style={{ position: 'fixed', top: mention.top, left: mention.left, zIndex: 400 }}
          onMouseDown={e => e.preventDefault()}>
          {filtered.map(u => (
            <button key={u.id} type="button" className="bd-mention-opt" onClick={() => insertMention(u)}>
              <span className="bd-mention-name">{userLabel(u)}</span>
              <span className="bd-mention-email">{u.email}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>,
    document.body,
  );
};
